import { useSelector } from "react-redux";
import { UIRootState } from "ui/store";
import { translate } from "game/translate";
import "./styles.css";
import { StoryConfig } from "game/gameUI/UiHandlers/StoriesHandler/types";
import { useMemo } from "react";

interface StoriesListProps {
    onStoryClick?: (story: StoryConfig, storyNumber: number) => void;
    type?: 'available-stories' | 'completed-stories';
}

export const StoriesList = ({ onStoryClick, type }: StoriesListProps) => {
    const stories = useSelector((state: UIRootState) => state.gameUI.modals.storiesModal.stories);

    const lastCompletedStoryIndex = useMemo(() => {
        for (let i = stories.length - 1; i >= 0; i--) {
            const progress = parseInt(localStorage.getItem(`stories_story_${i + 1}_progress`) || '0');
            if (progress === stories[i].totalImages) {
                return i;
            }
        }
        return -1;
    }, [stories]);

    const list = useMemo(() => {
        return stories.map((story, index) => {
            const progress = parseInt(localStorage.getItem(`stories_story_${index + 1}_progress`) || '0');

            if (type === 'available-stories' && progress === story.totalImages) {
                return null;
            }

            if (type === 'completed-stories' && progress !== story.totalImages) {
                return null;
            }

            return { ...story, progress };
        });
    }, [stories, type]);

    const filteredList = useMemo(() => {
        return list.filter(Boolean);
    }, [list]);

    if (filteredList.length === 0) {
        return <span className="stories-list-empty">{type === 'available-stories' ? translate('stories_completed_text') : translate('stories_completed_empty_text')}</span>;
    }

    return (
        <div className="stories-list stories-scroll">
            {list.map((story, index) => {
                if (!story) {
                    return null;
                }

                const locked = type === 'available-stories' && index != lastCompletedStoryIndex + 1;

                return (
                    <div
                        key={story.id}
                        id={`story_${index + 1}`}
                        className={`story-card${locked ? ' locked' : ''}`}
                        data-story-number={index + 1}
                        data-total-images={story.totalImages}
                        data-story-id={story.id}
                        data-is-active={false}
                        data-is-completed={false}
                    >
                        <img className="story-card-image" src={story.coverImage} />
                        {locked && (
                            <div className="story-card-overlay">
                                <div className="lock-content">
                                    <div className="lock-icon-wrapper">
                                        <img className="lock-icon" src="/assets/stories/lock.webp" />
                                    </div>
                                    <p className="lock-text">{translate('stories_story_locked') || 'Locked'}</p>
                                    {(story.hasText || story.isPremium) && (
                                        <div className="story-info">
                                            {story.hasText && (
                                                <div className="story-tag">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                        <path d="M2.5 13V3C2.5 2.46957 2.71071 1.96086 3.08579 1.58579C3.46086 1.21071 3.96957 1 4.5 1H11.5C12.0304 1 12.5391 1.21071 12.9142 1.58579C13.2893 1.96086 13.5 2.46957 13.5 3V12.125C13.5 12.3571 13.4078 12.5796 13.2437 12.7437C13.0796 12.9078 12.8571 13 12.625 13H3.5C3.5 13.2652 3.60536 13.5196 3.79289 13.7071C3.98043 13.8946 4.23478 14 4.5 14H13C13.1326 14 13.2598 14.0527 13.3536 14.1464C13.4473 14.2402 13.5 14.3674 13.5 14.5C13.5 14.6326 13.4473 14.7598 13.3536 14.8536C13.2598 14.9473 13.1326 15 13 15H4.5C3.96957 15 3.46086 14.7893 3.08579 14.4142C2.71071 14.0391 2.5 13.5304 2.5 13ZM5.375 3C5.14294 3 4.92038 3.09219 4.75628 3.25628C4.59219 3.42038 4.5 3.64294 4.5 3.875V4.625C4.5 5.108 4.892 5.5 5.375 5.5H10.625C10.8571 5.5 11.0796 5.40781 11.2437 5.24372C11.4078 5.07962 11.5 4.85706 11.5 4.625V3.875C11.5 3.64294 11.4078 3.42038 11.2437 3.25628C11.0796 3.09219 10.8571 3 10.625 3H5.375Z" fill="white"/>
                                                    </svg>
                                                    <span>{translate('stories_tag_reading') || 'Reading'}</span>
                                                </div>
                                            )}
                                            {story.isPremium && (
                                                <div className="story-tag premium-tag">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none">
                                                        <path d="M3.52358 10.375C3.25154 10.375 3.01742 10.1828 2.96448 9.91593L1.80212 4.05697C1.69887 3.53654 2.30027 3.16972 2.71573 3.49972L5.28729 5.54224C5.55038 5.7512 5.93621 5.68844 6.1195 5.40687L8.0223 2.48383C8.24713 2.13846 8.75287 2.13845 8.9777 2.48383L10.8805 5.40687C11.0638 5.68844 11.4496 5.7512 11.7127 5.54224L14.2843 3.49971C14.6997 3.16972 15.3011 3.53654 15.1979 4.05697L14.0355 9.91593C13.9826 10.1828 13.7485 10.375 13.4764 10.375H3.52358ZM13.9444 12.4659C13.9444 12.9364 13.6333 13.25 13.1667 13.25H3.83333C3.36667 13.25 3.05556 12.9364 3.05556 12.4659V11.9904C3.05556 11.6756 3.31075 11.4205 3.62555 11.4205H13.3744C13.6892 11.4205 13.9444 11.6756 13.9444 11.9904V12.4659Z" fill="#FAFAFA"/>
                                                    </svg>
                                                    <span>{translate('stories_tag_premium') || 'Premium'}</span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {story.newStory && type !== 'completed-stories' && (
                            <div className="new-badge">
                                <svg xmlns="http://www.w3.org/2000/svg" width="68" height="45" viewBox="0 0 68 45" fill="none" preserveAspectRatio="xMinYMin meet">
                                    <path d="M0 0H68V42.2715C68 44.1083 66.2035 45.4073 64.4593 44.8318L34.8447 35.0613C34.2961 34.8803 33.7039 34.8803 33.1553 35.0613L3.54073 44.8318C1.7965 45.4073 0 44.1083 0 42.2715V0Z" fill="url(#paint0_linear_380_3056)"/>
                                    <defs>
                                        <linearGradient id="paint0_linear_380_3056" x1="53.3763" y1="40.663" x2="38.9934" y2="-4.6028" gradientUnits="userSpaceOnUse">
                                            <stop offset="0.312082" stopColor="#C81C3B"/>
                                            <stop offset="1" stopColor="#FE5A64"/>
                                        </linearGradient>
                                    </defs>
                                </svg>
                                <span>{translate('stories_new') || 'New'}</span>
                            </div>
                        )}

                        <div className="story-card-content" style={{display: locked ? 'none' : 'block'}}>
                            {(story.hasText || story.isPremium) && (
                                <div className="story-info">
                                    {story.hasText && (
                                        <div className="story-tag">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                <path d="M2.5 13V3C2.5 2.46957 2.71071 1.96086 3.08579 1.58579C3.46086 1.21071 3.96957 1 4.5 1H11.5C12.0304 1 12.5391 1.21071 12.9142 1.58579C13.2893 1.96086 13.5 2.46957 13.5 3V12.125C13.5 12.3571 13.4078 12.5796 13.2437 12.7437C13.0796 12.9078 12.8571 13 12.625 13H3.5C3.5 13.2652 3.60536 13.5196 3.79289 13.7071C3.98043 13.8946 4.23478 14 4.5 14H13C13.1326 14 13.2598 14.0527 13.3536 14.1464C13.4473 14.2402 13.5 14.3674 13.5 14.5C13.5 14.6326 13.4473 14.7598 13.3536 14.8536C13.2598 14.9473 13.1326 15 13 15H4.5C3.96957 15 3.46086 14.7893 3.08579 14.4142C2.71071 14.0391 2.5 13.5304 2.5 13ZM5.375 3C5.14294 3 4.92038 3.09219 4.75628 3.25628C4.59219 3.42038 4.5 3.64294 4.5 3.875V4.625C4.5 5.108 4.892 5.5 5.375 5.5H10.625C10.8571 5.5 11.0796 5.40781 11.2437 5.24372C11.4078 5.07962 11.5 4.85706 11.5 4.625V3.875C11.5 3.64294 11.4078 3.42038 11.2437 3.25628C11.0796 3.09219 10.8571 3 10.625 3H5.375Z" fill="white"/>
                                            </svg>
                                            <span>{translate('stories_tag_reading') || 'Reading'}</span>
                                        </div>
                                    )}
                                    {story.isPremium && (
                                        <div className="story-tag premium-tag">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none">
                                                <path d="M3.52358 10.375C3.25154 10.375 3.01742 10.1828 2.96448 9.91593L1.80212 4.05697C1.69887 3.53654 2.30027 3.16972 2.71573 3.49972L5.28729 5.54224C5.55038 5.7512 5.93621 5.68844 6.1195 5.40687L8.0223 2.48383C8.24713 2.13846 8.75287 2.13845 8.9777 2.48383L10.8805 5.40687C11.0638 5.68844 11.4496 5.7512 11.7127 5.54224L14.2843 3.49971C14.6997 3.16972 15.3011 3.53654 15.1979 4.05697L14.0355 9.91593C13.9826 10.1828 13.7485 10.375 13.4764 10.375H3.52358ZM13.9444 12.4659C13.9444 12.9364 13.6333 13.25 13.1667 13.25H3.83333C3.36667 13.25 3.05556 12.9364 3.05556 12.4659V11.9904C3.05556 11.6756 3.31075 11.4205 3.62555 11.4205H13.3744C13.6892 11.4205 13.9444 11.6756 13.9444 11.9904V12.4659Z" fill="#FAFAFA"/>
                                            </svg>
                                            <span>{translate('stories_tag_premium') || 'Premium'}</span>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="story-bottom">
                                <div className="story-bottom-left">
                                    <span className="story-bottom-left-title">{translate(story.titleKey)}</span>
                                    <div className="progress-wrapper">
                                        {story.progress === story.totalImages && (
                                            <div className="story-completed-badge">
                                                <span>{translate('stories_completed_tag')}</span>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                    <path d="M15.5308 4.51033C15.2999 4.27886 14.9249 4.27886 14.6941 4.51032L8.86439 10.3546C8.63351 10.5861 8.25855 10.5861 8.02767 10.3546L5.29004 7.61C5.05915 7.37853 4.6842 7.37853 4.45331 7.61L2.88208 9.18519C2.65201 9.41583 2.65201 9.78916 2.88207 10.0198L6.04017 13.1859L8.04338 15.1942C8.27426 15.4257 8.64922 15.4257 8.8801 15.1942L10.8676 13.2017L17.1177 6.93592C17.3478 6.70528 17.3478 6.33195 17.1177 6.1013L15.5308 4.51033Z" fill="#38963F"/>
                                                </svg>
                                            </div>
                                        )}
                                        {story.progress !== story.totalImages && (
                                            <div className="story-progress-bar">
                                                <div className="progress" style={{ width: `${(story.progress / story.totalImages) * 100}%` }} />
                                                <span className="progress-text">{`${story.progress}/${story.totalImages}`}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div id="closing-ignore-el" className="btn-primary" onClick={() => onStoryClick?.(story, index + 1)}>
                                    {translate('stories_view_button')}
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};